import { faAngleDoubleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { gql, useEnhancedQuery } from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import { notEmpty } from "@uplift-ltd/ts-helpers";
import DefaultErrorPage from "next/error";
import Head from "next/head";
import React, { Fragment, useMemo } from "react";
import { CatalogVisibilityEnum, PageIdType } from "__generated__/globalTypes";
import ProductCard from "components/PrivatePracticeTools/ProductCard";
import {
  PageQuery,
  PageQueryVariables,
  STATIC_PAGE_QUERY,
} from "components/StaticPageTemplate/queries";
import Container from "components/common/Container";
import CreditHoursBadge from "components/common/CreditHoursBadge/CreditHoursBadge";
import Link from "components/common/Link";
import { FullPageLoading } from "components/common/Loading";
import SectionHeader from "components/common/SectionHeader";
import TableOfContents from "components/common/TableOfContents";
import { BUNDLE_URL, COURSE_URL, PRE_LICENSING_COURSES_SLUG } from "constants/urls";
import { Optional } from "helpers/types";
import { useAnchorRef } from "hooks/useAnchorRef";
import { usePageMeta } from "hooks/usePageMeta";
import { useRouterQuery } from "hooks/useRouterQuery";
import HtmlContent from "../../common/HtmlContent";
import IndividualCourses from "./IndividualCourses";
import {
  ProductBundlesQuery__Nodes as ChildProduct,
  ProductBundlesQueryNodes as Product,
  ProductBundlesQueryQuery as ProductBundlesQuery,
  ProductBundlesQueryVariables,
} from "./__generated__/ProductBundles";
import styles from "./ProductBundles.module.css";

export type { ProductBundlesQuery, ProductBundlesQueryVariables };

export const PRODUCT_BUNDLES_QUERY = gql`
  query ProductBundlesQuery($slug: ID!) {
    productCategory(id: $slug, idType: SLUG) {
      id
      name

      # As of WooGraphQL 0.10.6, the where connection args are not actually working
      products(first: 100, where: { type: BUNDLE, visibility: VISIBLE }) {
        nodes {
          id
          canPurchaseWithCredits
          creditCost
          hasPurchased
          priceUSD
          name
          slug
          # As of WooGraphQL 0.10.6, catalogVisibility requires the edit post capability, so we've added our own to filter on
          catalogVisibilityZur

          bundleFields {
            shortBundleDescription
          }

          courses {
            id
            title
            priceUSD
            slug
            ceUnits
          }

          courseGroups {
            id
            title
          }
        }
      }

      children {
        nodes {
          id
          name
          slug

          products(where: { type: BUNDLE }) {
            nodes {
              id
              canPurchaseWithCredits
              creditCost
              description
              hasPurchased
              priceUSD
              name
              slug
              catalogVisibilityZur

              bundleFields {
                shortBundleDescription
              }

              courses {
                id
                title
                priceUSD
                slug
                ceUnits
              }
            }
          }
        }
      }
    }
  }
`;

type Bundle = Optional<Product, "courseGroups" | "bundleFields">;

interface ProductBundleProps {
  bundle: Bundle;
}

const ProductBundle = ({ bundle }: ProductBundleProps) => {
  const ref = useAnchorRef<HTMLDivElement>(bundle.slug);

  return (
    <div key={bundle.id} ref={ref}>
      <ProductCard
        product={bundle}
        href={makeUrl(BUNDLE_URL, { slug: bundle.slug as string })}
        productType="bundle"
      >
        {bundle.bundleFields?.shortBundleDescription && (
          <HtmlContent>{bundle.bundleFields?.shortBundleDescription}</HtmlContent>
        )}
        <FontAwesomeIcon icon={faAngleDoubleDown} className="text-zur-orange-100" />
        <div className={styles.bundleCourses}>
          {bundle.courses.map(course => (
            <div key={course.id} className={styles.course}>
              <Link
                as={makeUrl(COURSE_URL, { slug: course.slug as string, page: "" })}
                href="/courses/[slug]"
                className={styles.courseTitleLink}
              >
                {course.title}
              </Link>
              <div>
                <CreditHoursBadge size="sm" ceUnits={course.ceUnits} />
              </div>
            </div>
          ))}
        </div>
      </ProductCard>
    </div>
  );
};

interface ProductBundleListProps {
  bundles: Bundle[];
}

const ProductBundleList = ({ bundles }: ProductBundleListProps) => {
  return (
    <>
      {bundles.map(bundle => (
        <ProductBundle key={bundle.id} bundle={bundle} />
      ))}
    </>
  );
};

interface ProductBundlesProps {
  category: string;
}

const productSort = (a: Product | ChildProduct, b: Product | ChildProduct) => {
  return (a.name?.toLowerCase() || "").localeCompare(b.name?.toLowerCase() || "");
};

const notHidden = (product: Product | ChildProduct) =>
  product.catalogVisibilityZur !== CatalogVisibilityEnum.HIDDEN;

const ProductBundles: React.FC<ProductBundlesProps> = ({ category }) => {
  const { routerQuery } = useRouterQuery();
  const { data: staticPage, loading: pageLoading } = useEnhancedQuery<
    PageQuery,
    PageQueryVariables
  >(STATIC_PAGE_QUERY, {
    variables: { id: routerQuery.slug || "", idType: PageIdType.URI },
  });

  const { loading, data } = useEnhancedQuery<ProductBundlesQuery, ProductBundlesQueryVariables>(
    PRODUCT_BUNDLES_QUERY,
    {
      variables: { slug: category },
    }
  );

  const products = useMemo(() => {
    return data?.productCategory?.products?.nodes?.filter(notEmpty).sort(productSort) || [];
  }, [data?.productCategory?.products?.nodes]);

  usePageMeta(data?.productCategory?.name);

  if (loading || pageLoading) {
    return <FullPageLoading />;
  }

  const page = staticPage?.page || null;

  if (!data?.productCategory) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <DefaultErrorPage statusCode={404} />
      </>
    );
  }

  const hasSubcategories = !!data.productCategory.children?.nodes?.length;
  const tocCategories = hasSubcategories
    ? data.productCategory.children?.nodes?.filter(notEmpty).map(item => ({
        label: item.name || "",
        href: `#${item.slug}`,
        id: item.slug,
      })) || []
    : [];

  return (
    <div>
      {page?.title && page?.content && (
        <SectionHeader title={page?.title || ""} summary={page?.content || ""} />
      )}
      <Container>
        <div className={styles.catalogMainWrapper}>
          <div className={styles.catalogSubWrapper}>
            {(tocCategories.length && <TableOfContents items={tocCategories} singleColumn />) ||
              null}

            {routerQuery.slug === PRE_LICENSING_COURSES_SLUG && <IndividualCourses />}

            {hasSubcategories ? (
              data.productCategory.children?.nodes?.filter(notEmpty).map(productCategory => (
                <Fragment key={productCategory.id}>
                  <h3 className={styles.subHeading} id={productCategory.slug || ""}>
                    {productCategory.name}
                  </h3>
                  <ProductBundleList
                    bundles={
                      productCategory.products?.nodes
                        ?.filter(notEmpty)
                        .filter(notHidden)
                        .sort(productSort) || []
                    }
                  />
                </Fragment>
              ))
            ) : (
              <ProductBundleList bundles={products.filter(notEmpty).filter(notHidden) || []} />
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ProductBundles;
